import React from 'react'
import { Button } from '@/shared/ui'
import LoadingCandles from '@/shared/ui/LoadingCandles'
import { FreemiumPostUpgradeCard } from '@/modules/post-shared'
import type { ChatItem } from '../services/chatService'
import { ChatCard } from './ChatCard'
import { useChatCommunitySummary } from '../hooks/useChatCommunitySummary'

const LIST_BATCH = 6
const AUTOLOAD_ROOT_MARGIN = '160px 0px 0px 0px'

type ChatListProps = {
  items: ChatItem[]
  communityEnabled: boolean
  currentUserId: string | null
  canDeleteOthers: boolean
  focusChatId?: number | null
  focusMessageId?: number | null
  showFreemiumCard?: boolean
  hasMore: boolean
  loadingMore: boolean
  onLoadMore?: () => void
  onReload?: () => void
}

export function ChatList({
  items,
  communityEnabled,
  currentUserId,
  canDeleteOthers,
  focusChatId,
  focusMessageId,
  showFreemiumCard,
  hasMore,
  loadingMore,
  onLoadMore,
  onReload,
}: ChatListProps) {
  const [visibleCount, setVisibleCount] = React.useState(LIST_BATCH)
  const prevTotalRef = React.useRef(0)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null)
  const [autoLoadEnabled, setAutoLoadEnabled] = React.useState(true)

  React.useEffect(() => {
    const total = items.length
    const prevTotal = prevTotalRef.current
    prevTotalRef.current = total
    if (!total) return

    setVisibleCount((current) => {
      if (!current) return Math.min(LIST_BATCH, total)
      if (current > total) return total
      if (prevTotal > 0 && total > prevTotal && current >= prevTotal) {
        return Math.min(current + LIST_BATCH, total)
      }
      return current
    })
  }, [items.length])

  React.useEffect(() => {
    const focusId = focusChatId != null ? Number(focusChatId) : null
    if (!focusId || !Number.isFinite(focusId) || focusId <= 0) return

    const idx = items.findIndex((it) => Number(it.id) === focusId)
    if (idx < 0) return

    const required = idx + 1

    setVisibleCount((current) =>
      current >= required ? current : Math.min(required, items.length),
    )
  }, [focusChatId, items])

  const visibleItems = React.useMemo(() => items.slice(0, visibleCount), [items, visibleCount])
  const shouldShowFreemiumCard = showFreemiumCard === true && visibleItems.length > 0
  const hasMoreToShow = visibleCount < items.length
  const showLoadMore = hasMoreToShow || (hasMore && !!onLoadMore)

  const visibleChatIds = React.useMemo(
    () =>
      visibleItems
        .map((it) => Number(it.id))
        .filter((id) => Number.isFinite(id) && id > 0),
    [visibleItems],
  )

  const { map: chatCommunitySummaryMap } = useChatCommunitySummary(
    visibleChatIds,
    communityEnabled,
  )

  const handleLoadMore = React.useCallback(() => {
    if (loadingMore) return

    if (visibleCount < items.length) {
      setVisibleCount((current) => Math.min(current + LIST_BATCH, items.length))
      return
    }

    if (!hasMore) return
    onLoadMore?.()
  }, [hasMore, items.length, loadingMore, onLoadMore, visibleCount])

  React.useEffect(() => {
    if (!showLoadMore) return
    if (!autoLoadEnabled) return

    const el = sentinelRef.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setAutoLoadEnabled(false)
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            handleLoadMore()
          }
        })
      },
      { rootMargin: AUTOLOAD_ROOT_MARGIN },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
    }
  }, [autoLoadEnabled, handleLoadMore, showLoadMore])

  if (!items.length) {
    return (
      <div className="mt-6 text-center text-sm text-foreground/70">
        Nenhum post publicado ainda. Use o botao &quot;Adicionar post&quot; para criar o primeiro.
      </div>
    )
  }

  return (
    <div className="mt-4 space-y-4">
      {visibleItems.map((item, index) => {
        const chatCommunitySummary = chatCommunitySummaryMap?.[String(item.id)] ?? null

        const effectiveFocusMessageId =
          focusChatId && Number(item.id) === Number(focusChatId)
            ? focusMessageId ?? null
            : null

        return (
          <React.Fragment key={`chat-${item.id}`}>
            <ChatCard
              chat={item}
              communityEnabled={communityEnabled}
              currentUserId={currentUserId}
              canDeleteOthers={canDeleteOthers}
              chatCommunitySummary={chatCommunitySummary}
              focusMessageId={effectiveFocusMessageId}
              onChanged={onReload}
            />
            {shouldShowFreemiumCard && index === 0 && (
              <FreemiumPostUpgradeCard moduleLabel="Chat" />
            )}
          </React.Fragment>
        )
      })}

      {showLoadMore &&
        (autoLoadEnabled ? (
          <div
            ref={sentinelRef}
            className="mt-4 flex w-full items-center justify-center py-2"
          >
            <LoadingCandles className="min-h-[80px]" label="Carregando mais posts..." />
          </div>
        ) : (
          <div className="flex justify-center pt-4">
            <Button
              type="button"
              variant="outline"
              disabled={loadingMore}
              onClick={handleLoadMore}
            >
              {loadingMore ? 'Carregando...' : hasMoreToShow ? 'Ver mais' : 'Carregar mais posts'}
            </Button>
          </div>
        ))}
    </div>
  )
}
